import { clientEnvModule } from "@/modules/client-env";
import { Hotkey } from "@tanstack/react-hotkeys";
import {
  HotkeyByPlatform,
  ShortcutBrowserFallback,
  ShortcutBinding,
  ShortcutPlatformBucket,
} from "@/modules/keyboard-shortcuts/registry";

const applyBrowserFallbackToModHotkey = (hotkey: Hotkey): Hotkey => {
  // Tanstack Hotkeys enforces ordering of modifiers via type checks,
  // so checking the start of the string is sufficient.
  if (!hotkey.startsWith("Mod+")) {
    return hotkey;
  }

  const fallbackModifier = clientEnvModule.isMacBrowser() ? "Control" : "Alt";
  return hotkey.replace("Mod+", `${fallbackModifier}+`) as Hotkey;
};

const resolveShortcutPlatformBucket = (): ShortcutPlatformBucket => {
  if (clientEnvModule.isMacOs() || clientEnvModule.isIos()) {
    return "apple";
  }

  // Windows, Linux, Android, etc.
  return "pc";
};

const isSplitShortcutBinding = (
  binding: ShortcutBinding
): binding is ShortcutBinding & { hotkeyByPlatform: HotkeyByPlatform } =>
  binding.hotkeyByPlatform !== undefined;

const resolvePrimaryHotkey = (
  binding: ShortcutBinding,
  platformBucket: ShortcutPlatformBucket
): Hotkey => {
  if (isSplitShortcutBinding(binding)) {
    return binding.hotkeyByPlatform[platformBucket];
  }

  return binding.hotkey;
};

const resolveExplicitBrowserFallbackHotkey = (
  browserFallback: ShortcutBrowserFallback,
  platformBucket: ShortcutPlatformBucket
): Hotkey | undefined => {
  if (browserFallback === true) {
    return undefined;
  }

  return browserFallback[platformBucket];
};

// Get the hotkey for the given shortcut, applying browser fallback if necessary
export const resolveHotkey = (binding: ShortcutBinding): Hotkey => {
  const platformBucket = resolveShortcutPlatformBucket();
  const primaryHotkey = resolvePrimaryHotkey(binding, platformBucket);
  const browserFallback = binding.browserFallback;

  if (clientEnvModule.isDesktop() || !browserFallback) {
    return primaryHotkey;
  }

  const explicitBrowserFallbackHotkey = resolveExplicitBrowserFallbackHotkey(
    browserFallback,
    platformBucket
  );
  if (explicitBrowserFallbackHotkey) {
    return explicitBrowserFallbackHotkey;
  }

  return applyBrowserFallbackToModHotkey(primaryHotkey);
};
